import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getGenreVisual } from '../data/genreIcons';
import catalogFallback from '../data/catalog-data.json';
import { useToast } from './Toast';
import { usePS2Graphics } from '../context/PS2GraphicsContext';
import Ps2GameBadge from './Ps2GameBadge';

const FAVORITES_KEY = 'tcc_favorite_games';

function loadFavorites() {
  try {
    return JSON.parse(localStorage.getItem(FAVORITES_KEY)) || [];
  } catch { return []; }
}

/**
 * GameCatalog — Displays the full game library with game favoriting support.
 * Fetches from /api/catalog (server-managed) with static fallback.
 */
export default function GameCatalog() {
  const [games, setGames] = useState(catalogFallback);
  const [favorites, setFavorites] = useState(loadFavorites);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [meta, setMeta] = useState({
    genres: [...new Set(catalogFallback.map(g => g.genre))].filter(Boolean).sort(),
    hosts: [...new Set(catalogFallback.map(g => g.host))].filter(Boolean).sort(),
    regions: [...new Set(catalogFallback.map(g => g.region))].filter(Boolean).sort(),
  });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [host, setHost] = useState('');
  const [genre, setGenre] = useState('');
  const [region, setRegion] = useState('');
  const toast = useToast();

  // Fetch live catalog + metadata from backend
  useEffect(() => {
    Promise.all([
      fetch('/api/catalog').then(r => r.json()).catch(() => []),
      fetch('/api/catalog/meta').then(r => r.json()).catch(() => ({ genres: [], hosts: [], regions: [] })),
    ]).then(([catalogData, metaData]) => {
      const validCatalog = (Array.isArray(catalogData) && catalogData.length > 0) ? catalogData : catalogFallback;
      setGames(validCatalog);

      const genres = (metaData.genres && metaData.genres.length > 0)
        ? metaData.genres
        : [...new Set(validCatalog.map(g => g.genre))].filter(Boolean).sort();
      const hosts = (metaData.hosts && metaData.hosts.length > 0)
        ? metaData.hosts
        : [...new Set(validCatalog.map(g => g.host))].filter(Boolean).sort();
      const regions = (metaData.regions && metaData.regions.length > 0)
        ? metaData.regions
        : [...new Set(validCatalog.map(g => g.region))].filter(Boolean).sort();

      setMeta({ genres, hosts, regions });
      setLoading(false);
    });
  }, []);

  const toggleFavorite = useCallback((gameName) => {
    setFavorites(prev => {
      const next = prev.includes(gameName)
        ? prev.filter(f => f !== gameName)
        : [...prev, gameName];
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
      if (next.includes(gameName)) {
        toast.success(`❤️ Added "${gameName}" to favorites`);
      } else {
        toast.info(`Removed "${gameName}" from favorites`);
      }
      return next;
    });
  }, [toast]);

  // Client-side filtering for instant results
  const filteredGames = useMemo(() => {
    let results = games;
    if (showFavoritesOnly) {
      results = results.filter(g => favorites.includes(g.name));
    }
    if (search) {
      const q = search.toLowerCase();
      results = results.filter(g =>
        g.name.toLowerCase().includes(q) ||
        g.dns.toLowerCase().includes(q) ||
        g.genre.toLowerCase().includes(q)
      );
    }
    if (host) results = results.filter(g => g.host === host);
    if (genre) results = results.filter(g => g.genre === genre);
    if (region) results = results.filter(g => g.region === region);
    return results;
  }, [games, search, host, genre, region, favorites, showFavoritesOnly]);

  return (
    <section id="available-games" className="relative bg-gradient-to-b from-black/60 via-black/75 to-black/90 backdrop-blur-md px-4 md:px-6 py-10 md:py-16">
      {/* Soft top gradient divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-5xl mx-auto w-full">
        {/* Section header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/[0.1] to-transparent" />
            <span className="text-[10px] font-mono text-white/30 tracking-[0.3em] uppercase">Library</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/[0.1] to-transparent" />
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight text-center mb-1">
            Available Games
          </h2>
          <p className="text-center text-sm text-white/40">
            {loading ? 'Loading catalog...' : `${games.length} titles across all platforms and genres`}
          </p>
        </div>

        {/* Search & Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              id="game-search"
              type="text"
              placeholder="Search games, DNS, or genre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm font-mono bg-white/[0.06] border border-white/[0.1] text-white/90 placeholder-white/30 focus:outline-none focus:border-emerald-500/40 transition-colors"
            />
          </div>

          {/* Favorites Filter Button */}
          <button
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            className={`px-3.5 py-2.5 rounded-lg text-xs font-mono transition-all flex items-center justify-center gap-1.5 cursor-pointer border ${
              showFavoritesOnly
                ? 'bg-red-500/20 text-red-300 border-red-500/40 shadow-[0_0_12px_rgba(239,68,68,0.2)] font-bold'
                : 'bg-white/[0.06] border-white/[0.1] text-white/70 hover:text-white hover:bg-white/[0.1]'
            }`}
            title="Filter by favorited games"
          >
            <svg className={`w-3.5 h-3.5 ${showFavoritesOnly ? 'text-red-400' : 'text-white/40'}`} fill={showFavoritesOnly ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <span>Favorites ({favorites.length})</span>
          </button>

          {/* Host Filter */}
          <select
            id="host-filter"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            className="px-3 py-2.5 rounded-lg text-sm font-mono bg-white/[0.06] border border-white/[0.1] text-white/80 focus:outline-none focus:border-emerald-500/40 transition-colors cursor-pointer"
          >
            <option value="">All Hosts</option>
            {meta.hosts.map(h => (
              <option key={h} value={h}>{h}</option>
            ))}
          </select>

          {/* Genre Filter */}
          <select
            id="genre-filter"
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            className="px-3 py-2.5 rounded-lg text-sm font-mono bg-white/[0.06] border border-white/[0.1] text-white/80 focus:outline-none focus:border-emerald-500/40 transition-colors cursor-pointer"
          >
            <option value="">All Genres</option>
            {meta.genres.map(g => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>

          {/* Region Filter */}
          <select
            id="region-filter"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="px-3 py-2.5 rounded-lg text-sm font-mono bg-white/[0.06] border border-white/[0.1] text-white/80 focus:outline-none focus:border-emerald-500/40 transition-colors cursor-pointer"
          >
            <option value="">All Regions</option>
            {meta.regions.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        {/* Results count */}
        {!loading && (
          <div className="mb-4 text-[10px] font-mono text-white/40 tracking-wider uppercase flex items-center justify-between">
            <span>{filteredGames.length} {filteredGames.length === 1 ? 'game' : 'games'} found</span>
            {favorites.length > 0 && (
              <span className="text-white/30">Click ♡ on any title to favorite</span>
            )}
          </div>
        )}

        {/* Loading state — skeleton table */}
        {loading ? (
          <div className="space-y-0">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 py-3 border-b border-white/[0.03]" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="skeleton w-9 h-9 rounded-lg flex-shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="skeleton h-4" style={{ width: `${140 + Math.random() * 120}px` }} />
                  <div className="skeleton h-2.5" style={{ width: `${80 + Math.random() * 60}px` }} />
                </div>
                <div className="skeleton h-6 w-24 hidden sm:block" />
                <div className="skeleton h-6 w-20" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Game Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="pb-3 text-[10px] font-mono text-white/50 tracking-wider uppercase font-semibold">Game</th>
                    <th className="pb-3 text-[10px] font-mono text-white/30 tracking-wider uppercase font-semibold hidden sm:table-cell">Genre</th>
                    <th className="pb-3 text-[10px] font-mono text-white/50 tracking-wider uppercase font-semibold">DNS</th>
                    <th className="pb-3 text-[10px] font-mono text-white/30 tracking-wider uppercase font-semibold hidden md:table-cell text-center">Players</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGames.map((game) => (
                    <GameRow
                      key={game.id}
                      game={game}
                      isFavorite={favorites.includes(game.name)}
                      onToggleFavorite={() => toggleFavorite(game.name)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* No results */}
            {filteredGames.length === 0 && (
              <div className="text-center py-16">
                <p className="text-white/50 text-sm font-mono">No games found matching your filters</p>
                <button
                  onClick={() => { setSearch(''); setHost(''); setGenre(''); setRegion(''); setShowFavoritesOnly(false); }}
                  className="mt-3 text-emerald-400/60 hover:text-emerald-400 text-xs font-mono transition-colors"
                >
                  Clear filters →
                </button>
              </div>
            )}
          </>
        )}

        {/* Back to Top */}
        <div className="mt-10 text-center">
          <a
            href="#main-content"
            className="inline-flex items-center gap-2 px-4 py-2 text-[10px] font-mono text-white/30 hover:text-white/60 border border-white/[0.06] hover:border-white/[0.12] rounded-full transition-all duration-300"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
            Back To Top
          </a>
        </div>
      </div>
    </section>
  );
}

/**
 * Individual game row — table format with genre icon, name, DNS, max players, expandable spec sheet, and favorite toggle.
 */
function GameRow({ game, isFavorite, onToggleFavorite }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const genreVisual = useMemo(() => getGenreVisual(game.genre), [game.genre]);
  const toast = useToast();
  const { ps2Mode } = usePS2Graphics();

  const dnsInfo = useMemo(() => {
    const raw = game.dns || '';
    if (!raw || raw.trim().toUpperCase() === 'N/A') {
      return { primary: 'N/A', alt: null, isNA: true };
    }
    if (raw.includes(' or ')) {
      const parts = raw.split(' or ');
      return { primary: parts[0].trim(), alt: parts[1].trim(), isNA: false };
    }
    if (raw.includes('/') && !raw.toUpperCase().includes('N/A')) {
      const parts = raw.split('/');
      return { primary: parts[0].trim(), alt: parts[1].trim(), isNA: false };
    }
    return { primary: raw.trim(), alt: null, isNA: false };
  }, [game.dns]);

  const copyDns = (e) => {
    e?.stopPropagation();
    if (dnsInfo.isNA) return;
    navigator.clipboard.writeText(dnsInfo.primary).then(() => {
      setCopied(true);
      toast.info(`📋 Copied ${dnsInfo.primary} to clipboard`);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const regionColors = {
    'NTSC (US)': 'text-blue-400/80',
    'PAL (EU)': 'text-emerald-400/80',
    'NTSC (JP)': 'text-red-400/80',
    'NTSC (KO)': 'text-purple-400/80',
  };
  const regionColor = regionColors[game.region] || 'text-white/50';

  return (
    <>
      <tr
        onClick={() => setExpanded(!expanded)}
        className={`group border-b border-white/[0.05] hover:bg-white/[0.04] transition-colors cursor-pointer ${
          expanded ? (ps2Mode ? 'bg-cyan-950/20' : 'bg-emerald-950/20') : ''
        }`}
      >
        {/* Game Name with PS2 Game Box Art Badge & Favorite Button */}
        <td className="py-3 pr-4">
          <div className="flex items-center gap-3">
            {/* Favorite toggle + Expand Chevron + Box Art */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
                className="transition-transform duration-200 cursor-pointer hover:scale-115 p-1"
                title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <svg className={`w-3.5 h-3.5 transition-colors ${isFavorite ? 'text-red-400' : 'text-white/20 hover:text-red-400/50'}`} fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>

              <svg
                className={`w-3.5 h-3.5 text-white/30 group-hover:text-white/70 transition-transform duration-200 ${
                  expanded ? 'rotate-90 text-cyan-400' : ''
                }`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>

              <Ps2GameBadge gameId={game.id} gameName={game.name} genreIcon={genreVisual.icon} />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-white/90 group-hover:text-white transition-colors truncate">
                  {game.name}
                </span>
                {game.gameId && (
                  <span className="hidden sm:inline-block text-[9px] font-mono px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300/80 border border-cyan-500/20">
                    {game.gameId}
                  </span>
                )}
                {game.pcsx2Crc && (
                  <span className="hidden sm:inline-block text-[9px] font-mono px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300/80 border border-purple-500/20">
                    CRC: {game.pcsx2Crc}
                  </span>
                )}
                {/* DNAS Bypass Badge */}
                {game.dnasInfo && (game.dnasInfo.toLowerCase().includes('dnas') || game.dnasInfo.toLowerCase().includes('bypass') || game.dnasInfo.toLowerCase().includes('patch')) && (
                  <span
                    className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 font-semibold"
                    title={game.dnasInfo}
                  >
                    🔒 DNAS Bypass
                  </span>
                )}
                {/* XLink Kai Badge */}
                {(game.xlinkKai === 'YES' || game.comment?.toUpperCase().includes('XLINK KAI')) && (
                  <span
                    className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30 font-semibold"
                    title="Playable via XLink Kai LAN tunneling"
                  >
                    📡 XLink Kai
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5 sm:hidden">
                <span className="text-[10px] font-mono text-white/40">{game.genre}</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[10px] font-mono ${regionColor}`}>{game.region}</span>
                <span className="text-[10px] font-mono text-white/25">·</span>
                <span className="text-[10px] font-mono text-white/35">{game.host}</span>
                {game.voiceChat === 'YES' && (
                  <>
                    <span className="text-[10px] font-mono text-white/25">·</span>
                    <span className="text-[9px] font-mono text-emerald-400/80">VOICE CHAT</span>
                  </>
                )}
                {game.comment && (
                  <>
                    <span className="text-[10px] font-mono text-white/25">·</span>
                    <span className="text-[9px] font-mono text-white/40 truncate max-w-[200px]" title={game.comment}>
                      💬 {game.comment}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </td>

        {/* Genre */}
        <td className="py-3 pr-4 hidden sm:table-cell">
          <div className="flex items-center gap-1.5">
            <span
              className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ background: genreVisual.color }}
            />
            <span className="text-xs text-white/55">{game.genre}</span>
          </div>
        </td>

        {/* DNS */}
        <td className="py-3 pr-4">
          {dnsInfo.isNA ? (
            (game.xlinkKai === 'YES' || game.comment?.toUpperCase().includes('XLINK KAI')) ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-purple-500/10 border border-purple-500/20 text-xs font-mono text-purple-300/80" title="Playable via XLink Kai LAN tunneling">
                📡 XLink Kai
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded bg-white/[0.02] border border-white/[0.05] text-xs font-mono text-white/30 select-none">
                N/A
              </span>
            )
          ) : (
            <div>
              <button
                onClick={copyDns}
                className={`group/dns inline-flex items-center gap-1.5 px-2 py-1 rounded border transition-all duration-200 cursor-pointer ${
                  ps2Mode
                    ? 'bg-cyan-500/[0.05] border-cyan-500/20 hover:border-cyan-400/40 hover:bg-cyan-500/10'
                    : 'bg-white/[0.04] border-white/[0.08] hover:border-emerald-500/30 hover:bg-emerald-500/[0.04]'
                }`}
                title="Click to copy DNS"
                aria-label={`Copy DNS address ${dnsInfo.primary}`}
              >
                <code className={`text-xs font-mono transition-colors ${
                  ps2Mode
                    ? 'text-cyan-300/80 group-hover/dns:text-cyan-300'
                    : 'text-emerald-300/80 group-hover/dns:text-emerald-300'
                }`}>
                  {dnsInfo.primary}
                </code>
                {copied ? (
                  <svg className={`w-3 h-3 ${ps2Mode ? 'text-cyan-400' : 'text-emerald-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-3 h-3 text-white/30 group-hover/dns:text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
              {dnsInfo.alt && (
                <div className="text-[9px] font-mono text-white/25 mt-0.5">
                  alt: {dnsInfo.alt}
                </div>
              )}
            </div>
          )}
        </td>

        {/* Max Players */}
        <td className="py-3 hidden md:table-cell text-center">
          <span className="text-xs font-mono text-white/40">
            {game.maxPlayers > 0 ? `${game.maxPlayers}P` : '—'}
          </span>
        </td>
      </tr>

      {/* Expanded Technical Spec Panel */}
      {expanded && (
        <tr className={`border-b border-white/[0.08] ${ps2Mode ? 'bg-cyan-950/20' : 'bg-emerald-950/20'}`}>
          <td colSpan={4} className="p-4 md:p-6">
            <div
              className="rounded-xl p-4 md:p-5 border bg-black/50 backdrop-blur-md space-y-4 font-mono text-xs shadow-2xl transition-all duration-300"
              style={{
                borderColor: ps2Mode ? 'rgba(6,182,212,0.25)' : 'rgba(16,185,129,0.25)',
                boxShadow: ps2Mode ? '0 0 25px rgba(6,182,212,0.08)' : '0 0 25px rgba(16,185,129,0.08)'
              }}
            >
              {/* Spec Header */}
              <div className="flex items-start justify-between flex-wrap gap-4 border-b border-white/10 pb-3.5">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-white tracking-tight">{game.name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-white/60">
                      TECHNICAL SPEC SHEET
                    </span>
                  </div>
                  <p className="text-[11px] text-white/40 mt-1">
                    Revival Host: <span className="text-white/80 font-semibold">{game.host || 'N/A'}</span> · Region: <span className="text-white/80 font-semibold">{game.region}</span> · Max Players: <span className="text-white/80 font-semibold">{game.maxPlayers > 0 ? `${game.maxPlayers} Players` : 'N/A'}</span>
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {!dnsInfo.isNA && (
                    <button
                      onClick={copyDns}
                      className={`px-3 py-1.5 rounded-lg border font-mono text-xs flex items-center gap-2 cursor-pointer transition-all ${
                        ps2Mode
                          ? 'bg-cyan-500/15 border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25'
                          : 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25'
                      }`}
                    >
                      <span>📋 Copy Primary DNS:</span>
                      <strong className="underline">{dnsInfo.primary}</strong>
                    </button>
                  )}
                  {dnsInfo.alt && (
                    <span className="px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-white/50 text-[10px]">
                      Alt: {dnsInfo.alt}
                    </span>
                  )}
                </div>
              </div>

              {/* 4-Column Attribute Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                <div className="p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                  <span className="text-white/30 text-[9px] uppercase tracking-wider block">Game Serial / ID</span>
                  <span className="text-cyan-300 font-bold">{game.gameId || 'N/A'}</span>
                </div>
                <div className="p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                  <span className="text-white/30 text-[9px] uppercase tracking-wider block">PCSX2 CRC Hash</span>
                  <span className="text-purple-300 font-bold">{game.pcsx2Crc || 'N/A'}</span>
                </div>
                <div className="p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                  <span className="text-white/30 text-[9px] uppercase tracking-wider block">Playable Status</span>
                  <span className={game.playable === 'YES' ? 'text-emerald-400 font-bold' : 'text-amber-400 font-bold'}>
                    {game.playable === 'YES' ? '✅ PLAYABLE ONLINE' : '⚠️ XLINK / LIMITED'}
                  </span>
                </div>
                <div className="p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                  <span className="text-white/30 text-[9px] uppercase tracking-wider block">In-Game Net Config</span>
                  <span className="text-white/80 font-bold">{game.netConfig || 'N/A'}</span>
                </div>

                <div className="p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                  <span className="text-white/30 text-[9px] uppercase tracking-wider block">Crossplay Support</span>
                  <span className="text-white/80 font-bold">{game.crossplay || 'N/A'}</span>
                </div>
                <div className="p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                  <span className="text-white/30 text-[9px] uppercase tracking-wider block">Cross-Region Support</span>
                  <span className="text-white/80 font-bold">{game.crossRegion || 'N/A'}</span>
                </div>
                <div className="p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                  <span className="text-white/30 text-[9px] uppercase tracking-wider block">Voice Chat Support</span>
                  <span className={game.voiceChat === 'YES' ? 'text-emerald-400 font-bold' : 'text-white/40'}>
                    {game.voiceChat || 'NO'}
                  </span>
                </div>
                <div className="p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                  <span className="text-white/30 text-[9px] uppercase tracking-wider block">XLink Kai Compatibility</span>
                  <span className="text-purple-300 font-bold">{game.xlinkKai || 'NO'}</span>
                </div>
              </div>

              {/* DNAS Patch & Setup Info */}
              {game.dnasInfo && (
                <div className="p-3.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-200">
                  <div className="flex items-center gap-2 font-bold text-amber-300 text-xs mb-1">
                    <span>🔒 DNAS Patch & Setup Info:</span>
                  </div>
                  <p className="text-[11px] leading-relaxed whitespace-pre-wrap">{game.dnasInfo}</p>
                </div>
              )}

              {/* Community Comments & Hosting Notes */}
              {game.comment && (
                <div className="p-3.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white/70">
                  <div className="flex items-center gap-2 font-bold text-white/90 text-xs mb-1">
                    <span>💬 Community & Technical Notes:</span>
                  </div>
                  <p className="text-[11px] leading-relaxed whitespace-pre-wrap">{game.comment}</p>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
